import { Fragment } from 'react'

import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid'

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

interface SelectorIterface {
  id: number
  name: string
}

interface ButtonSelectorProps {
  selected: SelectorIterface | null
  setSelected: (_selected: SelectorIterface) => void
  data: SelectorIterface[]
  defaultSelected?: SelectorIterface
}

export default function ButtonSelector({
  selected,
  setSelected,
  data,
}: ButtonSelectorProps) {
  return (
    <Listbox
      by={(a, z) => a?.id === z?.id}
      onChange={setSelected}
      value={selected}
    >
      {({ open }) => (
        <>
          <div className="relative">
            <div
              className={
                'inline-flex divide-x divide-slate-100 rounded-md ' +
                'shadow-sm'
              }
            >
              <div
                className={
                  'inline-flex items-center gap-x-1.5 rounded-l-md bg-slate-200 px-3 py-2 ' +
                  'text-gray-600 shadow-sm'
                }
              >
                <CheckIcon aria-hidden="true" className="-ml-0.5 h-5 w-5" />
                <p className="line-clamp-1 text-sm font-semibold">
                  {selected?.name}
                </p>
              </div>
              <Listbox.Button
                className={
                  'inline-flex items-center rounded-l-none rounded-r-md bg-slate-200 p-2 ' +
                  'hover:bg-slate-300'
                }
              >
                <ChevronDownIcon
                  aria-hidden="true"
                  className="h-5 w-5 text-gray-600"
                />
              </Listbox.Button>
            </div>

            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              show={open}
            >
              <Listbox.Options
                className={
                  'absolute right-0 z-10 mt-2 w-72 origin-top-right divide-y divide-gray-200 ' +
                  'overflow-hidden rounded-md bg-white shadow-lg' +
                  'ring-1 ring-black ring-opacity-5 focus:outline-none'
                }
              >
                {data.map((option, idx) => (
                  <Listbox.Option
                    className={({ active }) =>
                      classNames(
                        active ? 'bg-slate-100 text-gray-500' : 'text-gray-900',
                        'cursor-default select-none p-4 text-sm'
                      )
                    }
                    key={idx}
                    value={option}
                  >
                    {({ selected, active }) => (
                      <div className="flex flex-col">
                        <div className="flex justify-between">
                          <p
                            className={
                              selected ? 'font-semibold' : 'font-normal'
                            }
                          >
                            {option.name}
                          </p>
                          {selected ? (
                            <span
                              className={
                                active ? 'text-gray-500' : 'text-slate-400'
                              }
                            >
                              <CheckIcon
                                aria-hidden="true"
                                className="h-5 w-5"
                              />
                            </span>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  )
}
